/**
 * Contract for the projects task endpoints.
 *
 * Imported by:
 *   - `excellent/app/projects/api/tasks/[id]/route.ts` (server)
 *   - `packages/ui/src/blocks/excellent/task-drawer.tsx`         (client)
 *
 * Schema names mirror the runtime shapes in `excellent/src/projects/types.ts`
 * (`ScopedTask`). The two must stay in lockstep — if a field is added to
 * `ScopedTask`, add it here too.
 */

import { z } from "zod";
import type { Operation } from "../types";
import {
  idNumericParams,
  paginatedSchema,
  paginationQuery,
  type PaginatedResult,
  type VerificationRun,
} from "./_common";
import {
  taskStatusSchema,
  taskPrioritySchema,
  tasksListFiltersSchema,
  type TaskStatusValue,
} from "@excellent/core/op-schemas";

export type { VerificationRun };
export type { TaskStatusValue };

/** RO-7-04: status/priority enums shared with MCP task_list via op-schemas. */
const taskStatus = taskStatusSchema;
const taskPriority = taskPrioritySchema;

export const scopedTaskSchema = z.object({
  id: z.number(),
  // ULID aggregate key (tasks.uid, v33). The integer `id` is local-only; `uid`
  // is the globally-unique handle the event engine keys a task's history on, so
  // the §6.5.5 EventTimeline mount can query /api/events by it. Optional because
  // a few hand-built fixtures (styleguide) construct ScopedTask literals without
  // it; every DB-backed row carries one (SCOPED_SELECT is `t.*`).
  uid: z.string().optional(),
  project_id: z.number(),
  section_id: z.number(),
  parent_id: z.number().nullable(),
  title: z.string(),
  description: z.string().nullable(),
  status: taskStatus,
  priority: taskPriority,
  assignee: z.string().nullable(),
  // Server-resolved display label for `assignee` ("Claude (shipper)") —
  // additive + display-only (the raw id stays the identity; the gate compares
  // ids). Optional: bulk reads and older fixtures don't compose it.
  assignee_label: z.string().nullable().optional(),
  due_date: z.string().nullable(),
  completed_at: z.string().nullable(),
  child_count: z.number(),
  sort_key: z.string(),
  archived_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  project_slug: z.string(),
  project_name: z.string(),
  project_color: z.string().nullable(),
  section_slug: z.string(),
  section_name: z.string(),
  // Optional goal linkage (goals are folded into Tasks). goal_id is the FK;
  // goal_slug/goal_title are joined for the drawer's Goal section link.
  goal_id: z.number().nullable(),
  goal_slug: z.string().nullable(),
  goal_title: z.string().nullable(),
  // Classification columns (v134 / UI-UX-PATTERNS Issue 5 / RO-8-03 = W5-A1).
  // Lifted out of title-marker pollution ([blocker]/[bug]/[human-sign-off]).
  // SCOPED_SELECT is `t.*` so the DB always returns them; without these keys
  // Hono's response validation (adapter.ts safeParse) strips them off the wire.
  // Nullable free-form today — vocabulary enforcement is W5-A3 (app-layer, not
  // a CHECK narrowing).
  kind: z.string().nullable().optional(),
  severity: z.string().nullable().optional(),
  // INTEGER NOT NULL DEFAULT 0 on the table; optional on fixtures that hand-build
  // ScopedTask literals without the column.
  human_gated: z.number().int().optional(),
  source_ref: z.string().nullable().optional(),
});

export type ScopedTask = z.infer<typeof scopedTaskSchema>;

export const tasksGet: Operation<
  { id: number },
  void,
  void,
  ScopedTask
> = {
  method: "GET",
  path: ({ id }) => `/projects/api/tasks/${id}`,
  params: idNumericParams,
  response: scopedTaskSchema,
};

export const tasksVerifications: Operation<
  { id: number },
  void,
  void,
  VerificationRun[]
> = {
  method: "GET",
  path: ({ id }) => `/projects/api/tasks/${id}/verifications`,
  params: idNumericParams,
  response: z.custom<VerificationRun[]>(),
};

/**
 * P2-U3 (verification-loop Wave B-UI): the task's verified-done evidence
 * artifact — the `evidence.artifact` action row the PRODUCTION emitter (P2-02,
 * `@excellent/core/live-sessions` evidence-emit) appended to the task's
 * agent-run trajectory. `found:false` is a data fact (no artifact emitted),
 * not a transport failure. This endpoint's URL doubles as the open/copy link
 * target: the resource IS the scrubbed artifact document.
 */
export const taskEvidenceArtifactSchema = z.object({
  found: z.boolean(),
  sessionId: z.number().nullable(),
  sessionUid: z.string().nullable(),
  seq: z.number().nullable(),
  /** Actor who emitted (= the verifier who marked the task done). */
  verifier: z.string().nullable(),
  /** Historical producer provenance retained for imported task evidence. */
  shipper: z.string().nullable(),
  emittedAt: z.string().nullable(),
  /** Named production call site from the payload envelope. */
  caller: z.string().nullable(),
  /** The scrubbed artifact document verbatim (already scrubbed at export). */
  artifact: z.unknown(),
});
export type TaskEvidenceArtifact = z.infer<typeof taskEvidenceArtifactSchema>;

export const tasksEvidenceArtifact: Operation<
  { id: number },
  void,
  void,
  TaskEvidenceArtifact
> = {
  method: "GET",
  path: ({ id }) => `/projects/api/tasks/${id}/evidence-artifact`,
  params: idNumericParams,
  response: taskEvidenceArtifactSchema,
};

/**
 * RO-7-04: list filters from `@excellent/core/op-schemas` so tasks.list and
 * task_list share the Operation substrate. paginationQuery merges page/page_size
 * (HTTP) with the shared filter atoms (project_id/section_id/status/q).
 */
export const tasksListQuery = paginationQuery.extend({
  project_id: tasksListFiltersSchema.shape.project_id,
  section_id: tasksListFiltersSchema.shape.section_id,
  status: tasksListFiltersSchema.shape.status,
  q: tasksListFiltersSchema.shape.q,
});
export type TasksListQuery = z.infer<typeof tasksListQuery>;

export const tasksListResponse = paginatedSchema(scopedTaskSchema);
export type TasksListResponse = PaginatedResult<ScopedTask>;

export const tasksList: Operation<
  void,
  z.infer<typeof tasksListQuery>,
  void,
  z.infer<typeof tasksListResponse>
> = {
  method: "GET",
  path: () => "/projects/api/tasks",
  query: tasksListQuery,
  response: tasksListResponse,
};

export const tasks = {
  list: tasksList,
  get: tasksGet,
  verifications: tasksVerifications,
  evidenceArtifact: tasksEvidenceArtifact,
};
